import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { notificationsApi } from '../lib/api';
import {
  ChatCircle,
  CheckSquare,
  Bell,
  Calendar,
  WhatsappLogo,
  User,
  SignOut,
  List,
  X,
  ChartBar,
  Translate,
  Moon,
  Sun,
  GearSix,
  UsersThree,
  ImageSquare
} from '@phosphor-icons/react';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useTheme } from '../context/ThemeContext';

const getNavItems = (t) => [
  { path: '/dashboard', icon: ChatCircle, label: t('aiChat') },
  { path: '/dashboard/tasks', icon: CheckSquare, label: t('tasks') },
  { path: '/dashboard/reminders', icon: Bell, label: t('reminders') },
  { path: '/dashboard/calendar', icon: Calendar, label: t('calendar') },
  { path: '/dashboard/team', icon: UsersThree, label: t('team') },
  { path: '/dashboard/statistics', icon: ChartBar, label: t('statistics') },
  { path: '/dashboard/image-analysis', icon: ImageSquare, label: t('imageAnalysis') },
  { path: '/dashboard/whatsapp-link', icon: WhatsappLogo, label: t('whatsApp') },
  { path: '/dashboard/profile', icon: User, label: t('profile') },
];

const LanguageToggle = () => {
  const { language, toggleLanguage } = useLanguage();
  
  return (
    <button
      onClick={toggleLanguage}
      className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
      title={language === 'en' ? 'Switch to Arabic' : 'التبديل إلى الإنجليزية'}
      data-testid="language-toggle-btn"
    >
      <Translate size={18} className="text-slate-600 dark:text-slate-300" />
      <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
        {language === 'en' ? 'العربية' : 'English'}
      </span>
    </button>
  );
};

const ThemeToggle = () => {
  const { isDark, toggleTheme } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
      title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      data-testid="theme-toggle-btn"
    >
      {isDark ? <Sun size={18} className="text-amber-400" /> : <Moon size={18} className="text-slate-600" />}
      <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
        {isDark ? 'Light' : 'Dark'}
      </span>
    </button>
  );
};

const NotificationBell = () => {
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const notificationsRef = useRef([]);
  const unreadCountRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        notificationsApi.getNotifications(),
        notificationsApi.getUnreadCount()
      ]);
      const newNotifs = notifRes.data.slice(0, 10);
      
      // Browser notification for new team events
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && unreadCountRef.current > 0) {
        const prev = notificationsRef.current;
        const newTeamNotifs = newNotifs.filter(n => !n.read && n.type?.startsWith('team') && !prev.find(o => o.notification_id === n.notification_id));
        if (newTeamNotifs.length > 0) {
          const latest = newTeamNotifs[0];
          new Notification(latest.title, { body: latest.message, icon: '/favicon.ico' });
        }
      }
      
      notificationsRef.current = newNotifs;
      unreadCountRef.current = countRes.data.count;
      setNotifications(newNotifs);
      setUnreadCount(countRes.data.count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkReminders = useCallback(async () => {
    try {
      const res = await notificationsApi.checkReminders();
      fetchNotifications();
      // Trigger browser notifications for new due reminders
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && res.data?.triggered > 0) {
        new Notification('Letsm AI Reminder', {
          body: `You have ${res.data.triggered} reminder${res.data.triggered > 1 ? 's' : ''} due now!`,
          icon: '/favicon.ico'
        });
      }
    } catch (error) {
      console.error('Error checking reminders:', error);
    }
  }, [fetchNotifications]);

  useEffect(() => {
    fetchNotifications();
    checkReminders();
    
    // Check for reminders every minute
    const interval = setInterval(() => {
      checkReminders();
    }, 60000);

    return () => clearInterval(interval);
  }, [fetchNotifications, checkReminders]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsApi.markAsRead(notificationId);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDelete = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await notificationsApi.deleteNotification(notificationId);
      fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button 
          className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="notification-bell"
        >
          <Bell size={22} className="text-slate-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#002FA7] text-white text-xs font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="border-b border-slate-200 p-3 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">{t('notifications')}</h3>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-xs text-[#002FA7] hover:underline"
              data-testid="mark-all-read"
            >
              {t('markAllRead')}
            </button>
          )}
        </div>
        
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">
              No notifications
            </div>
          ) : (
            notifications.map((notif) => (
              <div
                key={notif.notification_id}
                onClick={() => handleMarkAsRead(notif.notification_id)}
                className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                  !notif.read ? 'bg-blue-50/50' : ''
                }`}
                data-testid={`notification-${notif.notification_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {notif.type?.startsWith('team') && (
                        <span className="text-xs bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded font-medium">Team</span>
                      )}
                      <p className="text-sm font-medium text-slate-900">{notif.title}</p>
                    </div>
                    <p className="text-sm text-slate-600 truncate">{notif.message}</p>
                    <p className="text-xs text-slate-400 mt-1">{formatTime(notif.created_at)}</p>
                  </div>
                  <button
                    onClick={(e) => handleDelete(notif.notification_id, e)}
                    className="p-1 hover:bg-slate-200 rounded"
                  >
                    <X size={14} className="text-slate-400" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Sidebar = ({ onItemClick }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const navItems = getNavItems(t);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className={`h-full flex flex-col bg-slate-50 ${isRTL ? 'border-l' : 'border-r'} border-slate-200`}>
      {/* Logo */}
      <div className="p-6 border-b border-slate-200">
        <Link to="/dashboard" className="flex items-center gap-3" data-testid="sidebar-logo">
          <img
            src="/logo.jpeg"
            alt="AI by Let's M"
            className="h-8 w-auto rounded-lg"
          />
          <span className="font-bold text-slate-900 font-heading text-lg">Let's M <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">AI</span></span>
        </Link>
      </div>

      {/* Language & Theme Toggle */}
      <div className="px-4 pt-4 space-y-2">
        <LanguageToggle />
        <ThemeToggle />
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onItemClick}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                isActive
                  ? 'bg-white border border-slate-200 shadow-sm text-slate-900'
                  : 'text-slate-600 hover:bg-white hover:text-slate-900'
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
              <span className="font-medium text-sm">{item.label}</span>
            </Link>
          );
        })}

        {/* Admin Panel link — visible only for admin roles */}
        {['admin', 'developer', 'operations', 'viewer'].includes(user?.role) && (
          <Link
            to="/admin"
            onClick={onItemClick}
            className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-violet-600 hover:bg-violet-50 border border-dashed border-violet-200 mt-3"
            data-testid="nav-admin-panel"
          >
            <GearSix size={20} />
            <span className="font-medium text-sm">{t('adminPanel') || 'Admin Panel'}</span>
          </Link>
        )}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3 px-4 py-3">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
              <User size={16} className="text-slate-500" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>

        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 mt-2 text-slate-600 hover:bg-white hover:text-red-600 rounded-lg transition-colors"
          data-testid="logout-button"
        >
          <SignOut size={20} />
          <span className="font-medium text-sm">{t('signOut')}</span>
        </button>
      </div>
    </div>
  );
};

const DashboardLayout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { isRTL } = useLanguage();

  return (
    <div className={`h-screen flex bg-white ${isRTL ? 'flex-row-reverse' : ''}`}>
      {/* Desktop Sidebar */}
      <div className="hidden md:block w-64 flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-50">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img
            src="/logo.jpeg"
            alt="AI by Let's M"
            className="h-7 w-auto rounded-lg"
          />
          <span className="font-bold text-slate-900 font-heading">Let's M <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">AI</span></span>
        </Link>

        <div className="flex items-center gap-2">
          <NotificationBell />
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <button className="p-2 hover:bg-slate-100 rounded-lg" data-testid="mobile-menu-button">
                <List size={24} className="text-slate-700" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64">
              <Sidebar onItemClick={() => setMobileMenuOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Desktop Notification Bell - Top Right */}
      <div className="hidden md:block fixed top-4 right-6 z-40">
        <NotificationBell />
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto md:pt-0 pt-16">
        {children}
      </div>
    </div>
  );
};

export default DashboardLayout;
